import React, { ChangeEvent, FC, Fragment, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  ListSubheader,
  TextField,
  Typography,
  Grid,
  Box,
} from '@material-ui/core';
import {
  Autocomplete,
  AutocompleteRenderGroupParams,
  AutocompleteRenderInputParams,
} from '@material-ui/lab';
import { lighten, makeStyles } from '@material-ui/core/styles';
import { FieldArrayRenderProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { compact, orderBy } from 'lodash';
import SearchIcon from '@material-ui/icons/Search';

import { Group } from 'api';
import { UserGroup } from 'types/app';
import { useClient, useCurrentUser, useFollowed } from 'hooks';
import { GroupAvatar, PrivateGroupIcon, UserAvatar } from 'components';
import { USER_FEED_ID } from 'constants/index';
import { getModeratedGroups } from 'features/groups/helpers';

import { usePostContext } from '../../PostContext';
import { isOnlyModerator } from '../../helpers';

const useStyles = makeStyles(theme => ({
  tag: {
    padding: theme.spacing(0, 0.5, 0, 1),
  },
  doneButton: {
    padding: 0,
    minWidth: 'auto',
    '&:hover': {
      background: 'transparent',
      color: theme.palette.primary.main,
    },
  },
  listSubheader: {
    padding: theme.spacing(0.5, 1, 0, 1),
    textTransform: 'none',
  },
  divider: {
    margin: theme.spacing(0.5, 0),
  },
  empty: {
    backgroundColor: lighten(theme.palette.primary.light, 0.7),
    color: theme.palette.primary.dark,
    '&::-webkit-input-placeholder': {
      opacity: 1,
    },
    '&::-moz-placeholder': {
      opacity: 1,
    },
    '&:-ms-input-placeholder': {
      opacity: 1,
    },
    '&::-ms-input-placeholder': {
      opacity: 1,
    },
  },
  emptyBorder: {
    borderColor: lighten(theme.palette.primary.light, 0.7),
  },
  popupIndicatorEmpty: {
    color: theme.palette.primary.dark,
  },
}));

const useAutoCompleteStyles = makeStyles(theme => ({
  paper: {
    boxShadow: 'none',
    margin: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
  inputRoot: {
    backgroundColor: theme.palette.background.paper,
    flexWrap: 'nowrap',
  },
  input: {},
  popupIndicator: {},
  option: {
    padding: theme.spacing(0.5, 3, 0.5, 1),
    '&:hover, &[aria-selected="true"]': {
      background: 'transparent',
    },
  },
  listbox: {
    margin: 0,
    padding: 0,
  },
}));

interface IProps {
  selectedIds: string[];
  helpers: FieldArrayRenderProps;
}

const GroupDropdown: FC<IProps> = ({ helpers, selectedIds }) => {
  const classes = useStyles();
  const autocompleteClasses = useAutoCompleteStyles();
  const currentUser = useCurrentUser();
  const { disable_timeline } = useClient();
  const { t } = useTranslation();
  const { state } = usePostContext();
  const { postable, groups } = useFollowed();

  const { recent, list, timeline } = postable;

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [sortedGroups, setSortedGroups] = useState(getSortedGroups());
  const [tempSelectedGroups, setTempSelectedGroups] = useState(getSelectedGroups());

  const activeAutocompleteClasses = tempSelectedGroups.length
    ? autocompleteClasses
    : {
        ...autocompleteClasses,
        inputRoot: classes.empty,
        input: classes.empty,
        popupIndicator: classes.popupIndicatorEmpty,
      };

  useEffect(() => {
    setSortedGroups(getSortedGroups());
    setTempSelectedGroups(getSelectedGroups());
  }, [list]);

  function isGroupSelected(group: UserGroup) {
    return selectedIds.includes(group.id);
  }

  function isTimelineGroup(group: UserGroup) {
    return group.id === USER_FEED_ID;
  }

  function getSelectedGroups() {
    return selectedIds.map(id => sortedGroups.find(item => item.id === id));
  }

  function getGroupsForModerator() {
    const moderatedGroups = getModeratedGroups(currentUser, groups);
    const initialGroups = disable_timeline ? [] : [timeline];
    return state.moderatorMode ? initialGroups.concat(moderatedGroups) : list;
  }

  function getSortedGroups() {
    let nextSortedGroups = list;

    if (isOnlyModerator(currentUser)) {
      nextSortedGroups = getGroupsForModerator();
    }

    return orderBy(nextSortedGroups, group => selectedIds.includes(group.id), ['desc']);
  }

  function getPlaceholder() {
    if (isOpen) {
      return t('posting:searchGroups');
    }

    if (!tempSelectedGroups.length) {
      return t('posting:selectGroup');
    }

    return '';
  }

  function onClickToggle() {
    setIsOpen(!isOpen);
    setInputValue('');
    setSortedGroups(getSortedGroups());
    setTempSelectedGroups(getSelectedGroups());
  }

  function onChangeInput(event: ChangeEvent<HTMLInputElement>) {
    setInputValue(event.target.value);
  }

  function onClickGroupItem(group: UserGroup) {
    const currentIndex = selectedIds.findIndex(id => id === group.id);
    currentIndex >= 0 ? helpers.remove(currentIndex) : helpers.push(group.id);
  }

  function renderAvatar(group: UserGroup) {
    const isTimeline = isTimelineGroup(group);

    return isTimeline ? <UserAvatar user={currentUser} /> : <GroupAvatar group={group as Group} />;
  }

  function renderOptions(group: UserGroup) {
    const isSelected = isGroupSelected(group);

    return (
      <Grid key={group.id} container alignItems="center" onClick={() => onClickGroupItem(group)}>
        <Grid item xs={2}>
          {renderAvatar(group)}
        </Grid>
        <Grid item xs={9} container>
          {group.is_private && (
            <Box mr={0.5}>
              <PrivateGroupIcon group={group} type="list" />
            </Box>
          )}
          <Grid item xs zeroMinWidth>
            <Typography title={group.title} noWrap variant={isSelected ? 'body2' : 'body1'}>
              {group.title}
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs={1}>
          <Checkbox checked={isSelected} color="primary" />
        </Grid>
      </Grid>
    );
  }

  function renderInput(params: AutocompleteRenderInputParams) {
    let { startAdornment, endAdornment } = params.InputProps;
    if (isOpen) {
      startAdornment = <SearchIcon />;
      endAdornment = (
        <Button className={classes.doneButton} color="primary" onClick={onClickToggle}>
          {t('common:done')}
        </Button>
      );
    }

    return (
      <TextField
        {...params}
        value={inputValue}
        variant="outlined"
        placeholder={getPlaceholder()}
        onChange={onChangeInput}
        fullWidth
        InputProps={{
          ...params.InputProps,
          classes: { notchedOutline: !tempSelectedGroups.length ? classes.emptyBorder : '' },
          startAdornment,
          endAdornment,
        }}
      />
    );
  }

  function renderTags(value: UserGroup[]) {
    const compactedValue = compact(value);

    if (!compactedValue.length) {
      return null;
    }

    const count = compactedValue.length - 1;
    const group = compactedValue[0];

    return (
      <Fragment>
        <Typography
          noWrap
          key={group.id}
          variant="body2"
          title={group.title}
          onClick={onClickToggle}
          className={classes.tag}>
          {group.title}
        </Typography>
        {!!count && (
          <Typography variant="body2" onClick={onClickToggle}>
            {`+${count}`}
          </Typography>
        )}
      </Fragment>
    );
  }

  function groupBy(group: UserGroup) {
    const isSelected = tempSelectedGroups.includes(group);
    const isTimeline = isTimelineGroup(group);
    const isRecentGroup = recent.includes(group);

    if (isSelected) {
      return t('posting:postingTo');
    }

    if (isTimeline) {
      return t('common:timeline');
    }

    if (isRecentGroup) {
      return t('posting:recentGroups');
    }

    return t('posting:otherGroups');
  }

  function renderGroup(params: AutocompleteRenderGroupParams) {
    return (
      <Fragment key={params.key}>
        <ListSubheader key={params.key} disableSticky className={classes.listSubheader}>
          {params.group}
        </ListSubheader>
        {params.children}
        <Divider className={classes.divider} />
      </Fragment>
    );
  }

  return (
    <Autocomplete
      classes={activeAutocompleteClasses}
      open={isOpen}
      onOpen={onClickToggle}
      onClose={onClickToggle}
      openOnFocus
      multiple
      fullWidth
      disableClearable
      disableCloseOnSelect
      forcePopupIcon={!isOpen}
      options={sortedGroups}
      getOptionLabel={group => group.title}
      renderOption={renderOptions}
      renderInput={renderInput}
      renderTags={renderTags}
      value={tempSelectedGroups}
      inputValue={inputValue}
      groupBy={groupBy}
      renderGroup={renderGroup}
    />
  );
};

export default GroupDropdown;
